import ListFilters from "@/common/components/ListFilters";

import TextField from "@/common/components/TextField";
import { SearchIcon } from "lucide-react";

import useDebounce from "@/common/hooks/useDebounce";
import Button from "@/common/components/Button";

import Table from "@/common/components/Table";
import { useParams } from "react-router-dom";

import { useEffect, useState } from "react";


import { Mapper } from "@/common/services/mapper";
import { useProductCategories } from "@/common/hooks/queries/useProductCategories";
import { ProductCategoryData } from "@/common/types/productCategory.type";
import useEstablishmentCompany from "@/features/establishments/hooks/useEstablishmentCompany";
import ModalToAddProduct from "@/features/establishments/components/ModalToAddProduct/modalToAddProduct";
import { ProductPriceEditType } from "@/common/types/productPriceEdit";
import ModalToEditProduct from "@/features/establishments/components/ModalToEditProduct/modalToEditProduct";
import { defaultColumn } from "@/features/establishments/constants/establishmentProductsColumns";
import { EstablishmentProductPrice, EstablishmentProductPriceTableItem } from "@/features/establishments/types/establishmentProducts.type";
import { useEstablishmentProducts } from "@/features/establishments/hooks/useEstablishmentProducts";
import { EstablishmentProductPriceTransformer } from "@/features/establishments/services/establishmentProductPriceTransformer";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";


const List = () => {
  const { filterParams, setFilterParams } = useFilterParams();

  const [search, setSearch] = useDebounce(filterParams.search || '');
  const [searchProductCategories, setSearchProductCategories] = useDebounce('');

  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isCreationModalVisible, setIsCreationModalVisible] = useState(false);

  const [productPriceToEdit, setProductPriceToEdit] = useState<ProductPriceEditType | undefined>();

  const { id } = useParams();

  const { data: establishmentCompany } = useEstablishmentCompany(id!);

  const openModal = (productPriceToEdit: ProductPriceEditType) => {
    setProductPriceToEdit(productPriceToEdit);
    setIsEditModalVisible(true);
  };

  useEffect(() => {
    if (!isEditModalVisible) setProductPriceToEdit(undefined);
  }, [isEditModalVisible]);

  const {
    data: productsPrice,
    meta,
    isPending: establishmentProductsIsPending,
    error: establishmentProductsError = {} as any,
    refetch: refetchEstablishmentProducts,
  } = useEstablishmentProducts<EstablishmentProductPrice[]>({
    filters: {
      search,
      page: filterParams.page || 1,
      productCategory: filterParams.productCategory?.value,
      companyBranch: establishmentCompany?.matrix_branch_id
    },
    enabled: !!establishmentCompany?.matrix_branch_id,
    mapper: Mapper.mapWithTransformer<EstablishmentProductPrice[], EstablishmentProductPriceTableItem[]>({
      transformer: EstablishmentProductPriceTransformer.tableTransformer(openModal) as any
    })
  });

  const { data: productCategoryOptions } = useProductCategories({
    filters: { search: searchProductCategories },
    mapper: Mapper.mapToOptions<ProductCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  });

  const getAllProductCategories = () => {
    return [...productCategoryOptions?.map((option) => ({ ...option, type: 'ProductCategory' })) || []];
  };

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-192px)]"
      statusCode={establishmentProductsError?.status}
      refetchFunction={refetchEstablishmentProducts}
    >
      <div className="flex flex-col gap-6 h-[calc(100vh-192px)]">
        <ModalToAddProduct
          isModalVisible={isCreationModalVisible}
          setIsModalVisible={setIsCreationModalVisible}
          branchId={establishmentCompany?.matrix_branch_id}
          companyProductCategories={establishmentCompany?.company_product_categories}
          productsPrices={productsPrice}
        />

        {isEditModalVisible &&
          <ModalToEditProduct
            isModalVisible={isEditModalVisible}
            setIsModalVisible={setIsEditModalVisible}
            productPriceToEdit={productPriceToEdit}
          />
        }

        <TextField
          LeftIcon={<SearchIcon />}
          placeholder="Pesquisar"
          className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
          onChange={(e) => {
            setSearch((e.target as HTMLInputElement).value);
            setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }));
          }}
          value={filterParams.search}
        />

        <div className="flex justify-between items-center z-50">
          <ListFilters
            filters={[
              {
                className: 'w-44',
                onInputChange: setSearchProductCategories,
                onChange: (productCategory) => setFilterParams((prev) => ({ ...prev, productCategory })),
                value: filterParams.productCategory,
                options: [{ label: 'Todos', value: '' }, ...getAllProductCategories()],
                placeholder: 'Serviço'
              }
            ]}
          />

          <Button
            name="create_product_price"
            className="py-2 px-4 rounded-sm cursor-pointer bg-primary-blue hover:bg-primary-blue-hover shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
            onClick={() => setIsCreationModalVisible(true)}
            disabled={establishmentProductsIsPending}
          >
            Adicionar novo produto
          </Button>
        </div>

        <Table
          rows={productsPrice}
          columns={defaultColumn}
          page={filterParams.page || 1}
          isLoadingData={establishmentProductsIsPending}
          totalPages={meta.total_pages}
          totalCount={meta.total}
          hasPagination
          setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
        />
      </div>
    </ErrorMessageWrapper>
  );
};

export default List;
